import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Pagination from '@travel/traveler-core/components/DomesticPagination';

import ProviderCard from 'promotions/components/ProviderCard';
import SEO from 'promotions/core/components/SEO';
import paths from 'promotions/core/universalRouter/paths';
import { fetchMoreSearchVacant } from 'promotions/store/searchVacant/actions';
import { getData, getIsFetchingMore, getProviders } from 'promotions/store/searchVacant/selectors';
import { buildURLQueryString, parseSearchVacantQuery } from 'promotions/utils/searchVacantUtils';

import styles from './datedAdPage.module.scss';

import { DATED_AD_PAGE_TITLE, SITE_IMAGE_URL } from 'promotions/constants/common';
import { pushLocation } from 'promotions/store/__router/actions';
import { APPEAR_DATED_AD, CLICK_DATED_AD } from 'promotions/utils/rat';

type Props = {
  search?: string;
  query?: any;
};

const DatedAdPage = (props: Props) => {
  const dispatch = useDispatch();
  const data = useSelector(getData);
  const isFetchingMore = useSelector(getIsFetchingMore);
  const providers = useSelector(getProviders);

  const { query } = props;
  const { total, offset, limit } = data;

  const onClickPaginationButton = useCallback(
    (newOffset: number, newLimit: number) => {
      const params = parseSearchVacantQuery(query);
      params.offset = newOffset;
      params.limit = newLimit;

      const queryString = buildURLQueryString(params);

      dispatch(
        pushLocation({
          pathname: paths.datedAd.path,
          search: queryString,
        }),
      );
    },
    [dispatch, query],
  );

  const onLoadNextPage = useCallback(() => {
    const nextOffset = offset + limit;

    const params = parseSearchVacantQuery(query);
    params.offset = nextOffset;
    params.limit = limit;

    dispatch(fetchMoreSearchVacant(params));
  }, [dispatch, query, offset, limit]);

  return (
    <div data-testid="datedAdPage-wrapper" className={styles.wrapper}>
      <SEO title={DATED_AD_PAGE_TITLE} imageUrl={SITE_IMAGE_URL} />

      {/* title */}
      <h1 className={styles.title}>注目宿泊施設</h1>

      {/* pagination info */}
      {total > 0 && (
        <div className={styles.paginationLabelWrapper}>
          <div className={styles.paginationTotalWrapper}>
            <span className={styles.paginationTotal}>{total}</span>
            <span>件中</span>
          </div>
          <div>
            {offset + 1}～{Math.min(offset + limit, total)} 件表示
          </div>
        </div>
      )}

      {/* Provider Cards */}
      <div className={styles.cardList}>
        {providers.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            appearEventType={APPEAR_DATED_AD}
            clickEventType={CLICK_DATED_AD}
          />
        ))}
      </div>

      {/* pagination  */}
      {total > 0 ? (
        <Pagination
          offset={offset}
          total={total}
          limit={limit}
          onClickPaginationButton={onClickPaginationButton}
          onLoadNextPage={onLoadNextPage}
          isFetching={isFetchingMore}
        />
      ) : null}
    </div>
  );
};

export default DatedAdPage;
